"use client";

import type { ProductForClient } from "@/lib/shared/types/interfaces";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, cn } from "@/ui";
import { ProductCard } from "./ProductCard";

interface ProductsSliderProps {
  products: ProductForClient[];
  /** When set, avoids a client settings fetch that can shift card aspect ratios. */
  imageRatio?: number;
  className?: string;
  /** Accessible name for the carousel region (e.g. translated section title). */
  ariaLabel?: string;
  /**
   * `top-overlay`: prev/next centered vertically on the strip (over the cards) so slides can use full width (no side padding).
   * `default`: same vertical centering; pair with horizontal padding on the carousel so arrows sit in the gutters.
   */
  arrowVariant?: "default" | "top-overlay";
}

/**
 * Horizontal product carousel (Embla) for related products and home strips.
 */
const topOverlayArrowClass =
  "top-1/2 left-2 -translate-y-1/2 z-10 border-border/80 bg-background/90 shadow-sm backdrop-blur-sm sm:left-3";

const topOverlayArrowNextClass =
  "top-1/2 right-2 -translate-y-1/2 z-10 border-border/80 bg-background/90 shadow-sm backdrop-blur-sm sm:right-3";

/** Horizontal product carousel (Embla) for related products and home strips. */
export const ProductsSlider = (props: ProductsSliderProps) => {
  const { products, imageRatio, className, ariaLabel, arrowVariant = "default" } = props;
  const isTopOverlay = arrowVariant === "top-overlay";

  if (products.length === 0) {
    return null;
  }

  return (
    <Carousel
      opts={{ align: "start", containScroll: "trimSnaps" }}
      aria-label={ariaLabel}
      className={cn("w-full", className)}
    >
      <CarouselContent className="-ml-2 items-stretch md:-ml-4">
        {products.map((product) => (
          <CarouselItem
            key={product.id}
            className="flex h-auto basis-1/2 pl-2 md:basis-1/4 md:pl-4"
          >
            <ProductCard
              className="flex h-full min-h-0 w-full flex-col"
              imageRatio={imageRatio}
              product={product}
            />
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious className={isTopOverlay ? topOverlayArrowClass : undefined} />
      <CarouselNext className={isTopOverlay ? topOverlayArrowNextClass : undefined} />
    </Carousel>
  );
};
